import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { AvatarPreset, EmptyState, LeaderboardRow } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { LeaderboardRow as Row } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { useActive } from "../state/hooks";
import { useSession } from "../state/session";
import { Shell } from "../components/Shell";
import { LoadingLine } from "../components/common";

/** Leaderboard (brief §6): live rows, rank deltas since last refresh, self pinned. */
export function LeaderboardScreen() {
  const t = useT();
  const trpc = useTRPC();
  const { locale } = useSession();
  const { active } = useActive();
  const query = useQuery({
    ...trpc.leaderboard.top.queryOptions({ eventId: active?.eventId ?? "", count: 10 }),
    enabled: Boolean(active),
    refetchInterval: 15_000,
  });

  // Rank deltas since the previous refresh (▲2 / ▼1).
  const previousRanks = useRef<Map<string, number>>(new Map());
  const [deltas, setDeltas] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!query.data) return;
    const next = new Map<string, number>();
    const all = [...query.data.rows, ...(query.data.self ? [query.data.self] : [])];
    const nextDeltas = new Map<string, number>();
    for (const row of all) {
      next.set(row.participationId, row.rank);
      const prev = previousRanks.current.get(row.participationId);
      if (prev !== undefined && prev !== row.rank) nextDeltas.set(row.participationId, prev - row.rank);
    }
    previousRanks.current = next;
    if (nextDeltas.size > 0) setDeltas(nextDeltas);
  }, [query.data]);

  const rows = query.data?.rows ?? [];
  const self = query.data?.self ?? null;
  const selfInRows = self ? rows.some((row) => row.participationId === self.participationId) : false;

  const renderRow = (row: Row) => {
    const delta = deltas.get(row.participationId);
    return (
      <LeaderboardRow
        key={row.participationId}
        rank={row.rank}
        name={row.isSelf ? `${row.displayName} · ${t("ec2.leaderboard.you")}` : row.displayName}
        avatar={<AvatarPreset preset={row.avatarPresetId} size={32} label={t("ec2.a11y.avatar", { name: row.displayName })} />}
        score={row.totalBucksEarned}
        delta={delta}
        deltaLabel={
          delta === undefined || delta === 0
            ? undefined
            : delta > 0
              ? t("ec2.a11y.rankUp", { count: delta })
              : t("ec2.a11y.rankDown", { count: Math.abs(delta) })
        }
        isSelf={row.isSelf}
        flareEdge={row.rank === 1}
        locale={locale}
      />
    );
  };

  return (
    <Shell active="ranks">
      <header className="flex items-center justify-between px-4 pb-3 pt-4">
        <h1 className="font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">
          {t("ec2.leaderboard.title")}
        </h1>
        <span className="flex items-center gap-1.5 text-[13px] font-medium text-fg-secondary">
          {t("ec2.leaderboard.live")}
          <span aria-hidden className="size-2 rounded-full bg-success" />
        </span>
      </header>
      <div className="flex flex-col gap-2 px-4" data-testid="ec-leaderboard">
        {query.isLoading && <LoadingLine />}
        {query.data && rows.length === 0 && (
          <EmptyState title={t("ec2.leaderboard.empty.title")} body={t("ec2.leaderboard.empty.body")} />
        )}
        {rows.map(renderRow)}
        {self && !selfInRows && (
          <div className="sticky bottom-[calc(78px+env(safe-area-inset-bottom))] pt-2" data-testid="ec-leaderboard-self">
            {renderRow(self)}
          </div>
        )}
      </div>
    </Shell>
  );
}
